import { AlertCircle, Check, Loader2, MoreHorizontal } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { useMoveJD, useReanalyse, type JDSource, type JDSummary } from "@/api/jd-queries";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Menu, type MenuItem } from "@/components/ui/menu";
import { ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";
import { experience, nextStep } from "@/lib/jd-steps";
import { cn, formatDate } from "@/lib/utils";

/**
 * The pieces of the job-description list — DESIGN §20.11. The strip of uploads still being read (or that
 * failed), which used to be rows of the JD table before they had any positions to show, and one JD's row
 * with its next step and its menu of the rarer moves.
 */

const NEXT_TONE = {
  ok: "text-bank-good-ink",
  warn: "text-bank-attention-ink",
  block: "text-bank-attention-ink font-semibold",
  plain: "text-bank-ink",
} as const;

export function Processing({ sources, canManage }: { sources: JDSource[]; canManage: boolean }) {
  const reanalyse = useReanalyse();
  if (sources.length === 0) return null;
  return (
    <Panel className="overflow-hidden">
      <PanelHeader
        title={`Processing · ${sources.length}`}
        actions={<span className="text-bank-muted text-xs">Uploads turn into positions below once read</span>}
      />
      {sources.map((src) => {
        const failed = src.status === "failed";
        return (
          <div
            key={src.id}
            className="border-bank-border flex flex-wrap items-center gap-x-3.5 gap-y-1.5 border-t px-4 py-2.5 first:border-t-0"
          >
            {failed ? (
              <AlertCircle className="text-bank-bad size-4" aria-hidden="true" />
            ) : (
              <Loader2 className="text-bank-pipe size-4 animate-spin" aria-hidden="true" />
            )}
            <span className="text-sm font-medium">{src.title || src.filename || "Pasted text"}</span>
            <Badge value={src.status} />
            <span className={cn("text-[12.5px]", failed ? "text-bank-bad-ink" : "text-bank-muted")}>
              {failed
                ? src.error
                : `Finding positions and skills · uploaded ${formatDate(src.created_at)} · you can leave this page`}
            </span>
            {failed && canManage && (
              <span className="ml-auto flex gap-1.5">
                <Button size="sm" disabled={reanalyse.isPending} onClick={() => reanalyse.mutate(src.id)}>
                  Try again
                </Button>
                <Link
                  to="/jds/new?mode=paste"
                  className="border-bank-border-strong bg-bank-surface hover:bg-bank-surface-2 inline-flex h-7 items-center rounded-md border px-2.5 text-xs font-medium"
                >
                  Paste the text instead
                </Link>
              </span>
            )}
          </div>
        );
      })}
      <div className="px-4 empty:hidden">
        <ErrorNote error={reanalyse.error} />
      </div>
    </Panel>
  );
}

export function JDRow({
  jd,
  canManage,
  canPurge,
  checked,
  onCheck,
  onDelete,
}: {
  jd: JDSummary;
  canManage: boolean;
  canPurge: boolean;
  checked: boolean;
  onCheck: () => void;
  onDelete: () => void;
}) {
  const navigate = useNavigate();
  const move = useMoveJD(jd.id);
  const next = nextStep(jd);
  const share = jd.skills_total > 0 ? jd.skills_covered / jd.skills_total : 0;
  const restricted = (jd.audience_members ?? []).length > 0 || (jd.audience_teams ?? []).length > 0;

  const MOVE: Partial<Record<JDSummary["status"], { to: JDSummary["status"]; label: string; hint: string }>> =
    {
      in_review: { to: "discarded", label: "Discard position", hint: "Out of use. Keep it brings it back." },
      active: { to: "archived", label: "Archive", hint: "Takes it off the start form." },
      archived: { to: "in_review", label: "Back to review", hint: "Publish it again from its page." },
      discarded: { to: "in_review", label: "Keep it", hint: "Back to review, like any other position." },
    };
  const alt = MOVE[jd.status];
  const items: (MenuItem | "divider")[] = [
    { label: "Open", onSelect: () => void navigate(`/jds/${jd.id}`) },
    { label: "Review & publish", onSelect: () => void navigate(`/jds/${jd.id}?step=publish`) },
  ];
  if (canManage && alt) {
    items.push({
      label: alt.label,
      hint: alt.hint,
      disabled: move.isPending,
      onSelect: () => move.mutate({ status: alt.to }),
    });
  }
  if (canPurge) {
    items.push("divider", {
      label: "Delete permanently…",
      hint: jd.status === "active" ? "Archive it first — a live JD can't be deleted." : undefined,
      disabled: jd.status === "active",
      danger: true,
      onSelect: onDelete,
    });
  }

  return (
    <tr
      className={cn(
        "border-bank-border hover:bg-bank-surface-2 border-t align-top transition-colors",
        checked && "bg-bank-accent-wash",
      )}
    >
      {canPurge && (
        <td className="px-3 py-2.5">
          <input
            type="checkbox"
            aria-label={`Select ${jd.title}`}
            className="accent-bank-accent"
            checked={checked}
            disabled={jd.status === "active"}
            title={jd.status === "active" ? "Archive it first to delete it." : undefined}
            onChange={onCheck}
          />
        </td>
      )}
      <td className="px-3 py-2.5">
        <Link to={`/jds/${jd.id}`} className="font-semibold hover:underline">
          {jd.title}
        </Link>
        {restricted && (
          <span
            className="bg-bank-accent-wash text-bank-accent-ink ml-2 rounded px-1.5 py-0.5 text-[10.5px]"
            title="Offered to chosen members and teams only"
          >
            chosen members
          </span>
        )}
        <p className="text-bank-muted max-w-72 truncate text-xs">
          {jd.client_name ?? "No client named"} ·{" "}
          <span className="font-mono text-[11px]">
            {jd.source_filename ?? jd.source_title ?? "pasted text"}
          </span>
        </p>
        <ErrorNote error={move.error} />
      </td>
      <td className="px-3 py-2.5">
        <Badge value={jd.status} />
      </td>
      <td className="px-3 py-2.5">
        <p className={cn("flex items-center gap-1.5 text-[13px]", NEXT_TONE[next.tone])}>
          {next.tone === "ok" && <Check className="size-4" aria-hidden="true" />}
          {next.tone === "block" && <AlertCircle className="size-4" aria-hidden="true" />}
          {next.text}
        </p>
        {next.detail && (
          <p
            className={cn(
              "max-w-56 truncate text-xs",
              next.tone === "block" ? "text-bank-attention-ink" : "text-bank-muted",
            )}
          >
            {next.detail}
          </p>
        )}
      </td>
      <td className="min-w-36 px-3 py-2.5">
        <div className="bg-bank-surface-3 h-1.5 overflow-hidden rounded-full">
          <div
            className={cn("h-full rounded-full", jd.stacks.length ? "bg-bank-accent" : "bg-bank-attention")}
            style={{ width: `${Math.round(share * 100)}%` }}
          />
        </div>
        <p className="text-bank-muted mt-1 font-mono text-[11px] tabular-nums">
          {jd.skills_covered} / {jd.skills_total}
          {jd.gaps > 0 && (
            <span className="text-bank-attention-ink">
              {" "}
              · {jd.gaps} gap{jd.gaps === 1 ? "" : "s"}
            </span>
          )}
        </p>
      </td>
      <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap tabular-nums">
        {jd.own_questions} own · {jd.matched_questions} matched
      </td>
      <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap">
        {jd.experience_min === null && jd.experience_max === null
          ? "—"
          : experience(jd.experience_min, jd.experience_max)}
      </td>
      <td className="text-bank-muted px-3 py-2.5 font-mono text-[11px] whitespace-nowrap">
        {formatDate(jd.updated_at)}
      </td>
      <td className="px-2 py-2">
        <Menu
          label={`More actions for ${jd.title}`}
          size="sm"
          variant="ghost"
          iconOnly
          trigger={<MoreHorizontal className="size-4" aria-hidden="true" />}
          items={items}
        />
      </td>
    </tr>
  );
}
